'use client';

import type { LucideIcon } from 'lucide-react';
import { ArrowUpRight, Heart, MessageCircle, Repeat2 } from 'lucide-react';
import { VerifiedBadge } from '@/components/shared/VerifiedBadge';
import { formatNumber } from '@/lib/utils';
import { formatDateTime, formatTimeAgo } from '@/lib/utils/date';
import { stripHandlePrefixes } from '@/lib/utils/regex';
import type { TweetToolResultData } from '../types';

export interface TweetResultCardProperties {
  result: TweetToolResultData;
}

type MetricDefinition = {
  label: string;
  icon: LucideIcon;
  value: number | null | undefined;
};

export function TweetResultCard({ result }: TweetResultCardProperties) {
  let createdAt: Date | null = null;
  if (result.createdAt) {
    createdAt = typeof result.createdAt === 'string' ? new Date(result.createdAt) : result.createdAt;
  }
  const sanitizedHandle = result.author ? stripHandlePrefixes(result.author) : null;
  const authorName = result.authorName || (sanitizedHandle ? `@${sanitizedHandle}` : 'Tweet');
  const username = sanitizedHandle ? `@${sanitizedHandle}` : null;
  const verifiedType = result.verifiedType && result.verifiedType !== 'none' ? result.verifiedType : null;

  const metricCandidates: MetricDefinition[] = [
    { label: 'Replies', icon: MessageCircle, value: result.replies },
    { label: 'Retweets', icon: Repeat2, value: result.retweets },
    { label: 'Likes', icon: Heart, value: result.likes },
  ];

  const metrics = metricCandidates.filter(
    (metric): metric is MetricDefinition & { value: number } => typeof metric.value === 'number' && metric.value >= 0
  );

  return (
    <div className="rounded-lg border border-border/70 bg-card/70 p-3 text-sm shadow-sm">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2 text-sm font-medium text-foreground">
            <span>{authorName}</span>
            {verifiedType ? <VerifiedBadge type={verifiedType} size="sm" /> : null}
          </div>
          {username ? <div className="text-xs text-muted-foreground">{username}</div> : null}
        </div>
        {createdAt ? (
          <div className="text-right text-xs text-muted-foreground" title={formatDateTime(createdAt)}>
            {formatTimeAgo(createdAt)}
          </div>
        ) : null}
      </div>

      {result.text ? <div className="mt-3 whitespace-pre-wrap text-sm text-foreground">{result.text}</div> : null}

      {metrics.length > 0 ? (
        <div className="mt-3 flex flex-wrap gap-4 text-xs text-muted-foreground">
          {metrics.map(({ label, icon: Icon, value }) => (
            <span key={label} className="inline-flex items-center gap-1">
              <Icon className="h-3.5 w-3.5" />
              <span className="font-medium text-foreground">{formatNumber(value)}</span>
              <span>{label}</span>
            </span>
          ))}
        </div>
      ) : null}

      <div className="mt-3 text-xs">
        <a
          href={`https://x.com/i/web/status/${result.id}`}
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-1 font-medium text-primary hover:underline"
        >
          View on X
          <ArrowUpRight className="h-3 w-3" />
        </a>
      </div>
    </div>
  );
}
